import {
  CalculatorIcon,
  CalendarRangeIcon,
  CompassIcon,
  HardHatIcon,
  LayersIcon,
  MapIcon,
  MountainSnowIcon,
  PlaneIcon,
  SatelliteDishIcon,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type Module = {
  number: string;
  title: string;
  subtitle: string;
  classroom: string;
  items: string[];
  result: string;
  icon: LucideIcon;
};

export const modules: Module[] = [
  {
    number: '1',
    title: 'Fundamentos de Topografía y Representación del Terreno',
    subtitle: 'La base que tus alumnos necesitan para entender todo lo demás',
    classroom:
      'El primer contacto con la topografía puede ser el más transformador o el más aburrido. Este tomo convierte un tema técnico en algo comprensible y relevante para tus alumnos desde el primer día.',
    items: [
      'Qué es la topografía y para qué sirve en el mundo real',
      'Cuáles son sus elementos principales y aplicaciones',
      'Cómo se organiza una medición correctamente',
      'Conceptos de escalas, coordenadas y orientación',
      'Material explicado de forma clara con ejemplos locales',
    ],
    result:
      'Tus alumnos entienden la topografía como una herramienta práctica y ordenada, no como un conjunto de términos imposibles de memorizar.',
    icon: CompassIcon,
  },
  {
    number: '2',
    title: 'Mediciones, Errores y Cálculos Topográficos',
    subtitle: 'Todo lo que necesitan saber sobre precisión y compensación',
    classroom:
      'Este es uno de los puntos donde más se traban los alumnos. Este tomo te da el material listo para que comprendan cómo funcionan las mediciones reales y qué hacer con los errores.',
    items: [
      'Principios de medición directa e indirecta',
      'Cálculo de distancias, ángulos y desniveles',
      'Precisión, exactitud y tolerancia',
      'Compensación de errores sistemáticos y accidentales',
      'Cálculo de escalas, pendientes, superficies y volúmenes',
      '50 ejercicios resueltos paso a paso',
    ],
    result: 'Tus alumnos aprenden a medir con método y a confiar en sus cálculos sin dudas constantes.',
    icon: CalculatorIcon,
  },
  {
    number: '3',
    title: 'Planimetría y Levantamientos Topográficos',
    subtitle: 'Cómo enseñar a levantar un terreno correctamente',
    classroom:
      'La planimetría es el corazón de la topografía. Este tomo te da todo lo que necesitas para que tus alumnos realicen levantamientos claros y ordenados.',
    items: [
      'Métodos de levantamiento: radiación, intersección y triangulación',
      'Poligonales abiertas y cerradas',
      'Uso de cinta, brújula, nivel y teodolito',
      'Introducción a la estación total',
      'Registro ordenado de datos de campo',
      'Cálculo y compensación de poligonales',
      '8 casos prácticos simulados listos para el aula',
    ],
    result: 'Tus alumnos saben qué método usar según el terreno y cómo registrar datos que sean útiles.',
    icon: MapIcon,
  },
  {
    number: '4',
    title: 'Altimetría, Nivelación y Perfiles del Terreno',
    subtitle: 'El módulo que completa la medición del territorio',
    classroom:
      'Este es el núcleo que falta en muchos cursos. Aquí tus alumnos aprenden a medir diferencias de altura y a crear perfiles del terreno.',
    items: [
      'Fundamentos de altimetría y cotas',
      'Nivelación geométrica y trigonométrica',
      'Lectura correcta de miras',
      'Cálculo de desniveles y control',
      'Perfiles longitudinales y transversales',
      'Curvas de nivel',
      'Pendientes y rasantes',
      'Libretas de nivelación listas para proyectar',
      '12 casos de aplicación en obras y terrenos',
    ],
    result: 'Tus alumnos cierran la comprensión tridimensional del territorio de forma clara.',
    icon: MountainSnowIcon,
  },
  {
    number: '5',
    title: 'Estaciones Totales, GNSS y Posicionamiento Satelital',
    subtitle: 'Tecnología moderna explicada de forma comprensible',
    classroom:
      'Muchos docentes evitan enseñar tecnología porque “no tienen instrumental”. Este tomo te enseña a conceptualizar sin necesidad de equipos caros.',
    items: [
      'Funcionamiento de estaciones totales',
      'Medición electrónica de distancias y ángulos',
      'Replanteo con tecnología',
      'Fundamentos de GPS y GNSS',
      'Métodos estático, cinemático y RTK',
      'Sistemas de coordenadas y transformaciones',
      'Fuentes de error en el posicionamiento',
      'Actividades sin instrumental profesional',
      '6 simulaciones listas para el laboratorio',
    ],
    result: 'Tus alumnos entienden la tecnología moderna sin sentir que la clase quedó atrás.',
    icon: SatelliteDishIcon,
  },
  {
    number: '6',
    title: 'Cartografía Digital, SIG y QGIS Aplicados',
    subtitle: 'Enseña cartografía digital desde cero',
    classroom: 'QGIS es gratuito y poderoso. Este tomo te da todo lo que necesitas para enseñarlo sin complicaciones.',
    items: [
      'Fundamentos de cartografía y proyecciones',
      'Datos vectoriales y ráster',
      'Introducción a Sistemas de Información Geográfica',
      'Organización de información territorial',
      'Uso educativo de QGIS (paso a paso)',
      'Creación e interpretación de mapas temáticos',
      'Georreferenciación de datos topográficos',
      'Análisis básico del terreno',
      '10 prácticas guiadas con archivos incluidos',
    ],
    result: 'Tus alumnos salen de tu clase sabiendo hacer mapas digitales reales.',
    icon: LayersIcon,
  },
  {
    number: '7',
    title: 'Drones, Fotogrametría y Modelos Digitales del Terreno',
    subtitle: 'La tecnología más moderna explicada educativamente',
    classroom: 'Los drones y la fotogrametría son el futuro. Este tomo te enseña a integrarlos sin ser un especialista.',
    items: [
      'Introducción a la fotogrametría',
      'Principios de captura aérea',
      'Planificación general de vuelos',
      'Superposición de imágenes y puntos de control',
      'Ortomosaicos y nubes de puntos',
      'Modelos digitales de elevación y superficie',
      'Precisión y fuentes de error',
      'Aplicaciones en obras, agricultura y territorio',
      '5 casos simulados para analizar en clase',
      'Consideraciones de seguridad y normativa',
    ],
    result: 'Tus alumnos comprenden la tecnología de punta sin confusión.',
    icon: PlaneIcon,
  },
  {
    number: '8',
    title: 'Topografía Aplicada a Obras, Caminos, Minería y Ámbitos Rurales',
    subtitle: 'La topografía en proyectos reales',
    classroom: 'Este tomo conecta todo lo aprendido con la realidad laboral de tus alumnos.',
    items: [
      'Relevamientos para obras civiles',
      'Replanteo de puntos, ejes y estructuras',
      'Perfiles para proyectos viales',
      'Cálculo básico de movimiento de suelos',
      'Mediciones en ámbitos rurales y agrícolas',
      'División y análisis de superficies',
      'Introducción a topografía minera',
      'Control topográfico de excavaciones',
      'Levantamientos urbanos',
      'Interpretación de planos de obra',
      '10 proyectos basados en situaciones latinoamericanas',
    ],
    result: 'Tus alumnos ven la topografía como la carrera práctica y útil que realmente es.',
    icon: HardHatIcon,
  },
  {
    number: '9',
    title: 'Enseñanza de la Topografía — Planificación y Proyectos 2026',
    subtitle: 'Tu guía completa como docente',
    classroom: 'Este tomo te da exactamente lo que necesitás para planificar, organizar y evaluar todo el año.',
    items: [
      'Enfoques actuales para enseñar topografía',
      'Organización progresiva de contenidos',
      'Planificación anual orientativa',
      'Secuencias didácticas completas',
      'Prácticas de aula y campo estructuradas',
      'Aprendizaje basado en proyectos',
      'Integración de herramientas digitales',
      'Trabajo con recursos limitados',
      'Evaluación teórica y práctica',
      'Rúbricas y criterios de observación',
      '8 proyectos integradores listos para adaptar',
    ],
    result: 'Nunca más te preguntás “¿qué doy hoy?”. Todo está planificado.',
    icon: CalendarRangeIcon,
  },
];
